// =====================================================
// Amplio - Sign Up Form Component
// Multi-tenant user registration with organization support
// =====================================================

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Eye, EyeOff, Lock, Mail, User, Building } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SwissLogo } from '@/components/ui/swiss-logo';

export function SignUpForm() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    role: 'partner' as const,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (formData.password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError('Bitte geben Sie Ihren Vor- und Nachnamen ein.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await signUp(formData.email, formData.password, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        organization_name: formData.organizationName,
        role: formData.role,
      });

      if (error) {
        setError(getErrorMessage(error.message));
        setLoading(false);
      } else {
        toast({
          title: "Registrierung erfolgreich",
          description: "Sie werden zur Anmeldung weitergeleitet...",
        });
        
        // Navigate to login after successful signup
        // Note: Don't set loading to false - let the page transition handle it
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Registrierung erfolgreich! Bitte melden Sie sich jetzt an.',
              email: formData.email // Pre-fill email on login page
            }
          });
        }, 500);
      }
    } catch (error) {
      setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      setLoading(false);
    }
  };

  const getErrorMessage = (error: string): string => {
    if (error.includes('User already registered')) {
      return 'Ein Benutzer mit dieser E-Mail-Adresse existiert bereits.';
    }
    if (error.includes('Password should be at least 6 characters')) {
      return 'Das Passwort muss mindestens 6 Zeichen lang sein.';
    }
    if (error.includes('Invalid email')) {
      return 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    }
    return 'Registrierung fehlgeschlagen. Bitte überprüfen Sie Ihre Daten.';
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md shadow-elegant">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <SwissLogo size="lg" />
          </div>
          <CardTitle className="text-2xl font-space-grotesk font-bold text-brand-primary">
            Amplio
          </CardTitle>
          <CardDescription className="text-muted-foreground font-inter">
            Erstellen Sie Ihr Konto
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="font-inter">{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Vorname *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => updateFormData('firstName', e.target.value)}
                    placeholder="Max"
                    className="pl-10"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Nachname *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  placeholder="Muster"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail-Adresse *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="max.muster@firma.ch"
                  className="pl-10"
                  required
                  autoComplete="email"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="organizationName">Firma (optional)</Label>
              <div className="relative">
                <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="organizationName"
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => updateFormData('organizationName', e.target.value)}
                  placeholder="Muster Treuhand AG"
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Rolle</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => updateFormData('role', value)}
                disabled={loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wählen Sie Ihre Rolle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assistant">Assistent/in</SelectItem>
                  <SelectItem value="junior">Junior Accountant</SelectItem>
                  <SelectItem value="senior">Senior Accountant</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="admin">Administrator/in</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  placeholder="Mindestens 8 Zeichen"
                  className="pl-10 pr-10"
                  required
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-brand-primary transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                  placeholder="Passwort wiederholen"
                  className="pl-10"
                  required
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-brand-primary hover:bg-brand-primary/90 text-brand-primary-foreground font-inter font-medium shadow-elegant"
              disabled={loading || !formData.email || !formData.password || !formData.firstName || !formData.lastName}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrieren...
                </>
              ) : (
                'Konto erstellen'
              )}
            </Button>

            <div className="text-center text-xs text-muted-foreground font-inter">
              Mit der Registrierung stimmen Sie unseren{' '}
              <Link to="/terms" className="text-brand-primary hover:text-brand-primary/80 transition-colors">
                Nutzungsbedingungen
              </Link>{' '}
              und der{' '}
              <Link to="/privacy" className="text-brand-primary hover:text-brand-primary/80 transition-colors">
                Datenschutzerklärung
              </Link>{' '}
              zu.
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-center text-sm text-muted-foreground font-inter">
              Bereits ein Konto?{' '}
              <Link
                to="/login"
                className="text-brand-primary hover:text-brand-primary/80 font-medium transition-colors"
              >
                Hier anmelden
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
